import { type FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { rateTypeTarget } from "../../utils/wageRules/rateTypeTarget";
import { RuleCard } from "./RuleCard/RuleCard";
import { RuleNameText } from "./RuleCard/RuleNameText";
import { isLegacyRuleTarget, type RuleCardSubject } from "./RuleCard/ruleCardSubjects";
import { WageSettingsLink } from "./WageSettingsLink";

type Props = {
	rowId: number;
	rateType: string;
	/**
	 * Whether the viewer may open Wage Settings. Off, the label stays plain text: no link to a tab they can't
	 * open, and no card of today's rules they couldn't read (a legacy row captured none to show instead).
	 */
	linkable: boolean;
	/**
	 * A paid segment of an agency shift, for a viewer who may open Agency Configuration: its card also shows
	 * the agency's row (its base is the bill rate).
	 */
	includeAgency: boolean;
};

/**
 * A wage breakdown row's rate type, linked to the Wage Settings rule that produced it. Rate types that
 * are not configured on Wage Settings (holiday, bonus, a salaried row, an unlabelled segment) stay plain
 * text rather than linking somewhere vague.
 *
 * Its hover card shows today's rows the rate type points at — a legacy row captured no rules, so this is
 * the closest the breakdown can come to "the setup behind it".
 */
export const RateTypeCell: FC<Props> = ({ rowId, rateType, linkable, includeAgency }) => {
	const { t } = useTranslation();
	const target = useMemo(() => rateTypeTarget(rateType), [rateType]);
	const subjects = useMemo<RuleCardSubject[]>(
		() => [
			...(linkable && target !== undefined && isLegacyRuleTarget(target) ? [{ kind: "legacy", target, rateType } as const] : []),
			...(includeAgency ? [{ kind: "agency" } as const] : []),
		],
		[linkable, target, rateType, includeAgency],
	);
	const card = subjects.length > 0 ? <RuleCard subjects={subjects} dataTestId={`wage-breakdown-rate-type-card-${rowId}`} /> : undefined;

	if (linkable && target) {
		return (
			<WageSettingsLink
				target={target}
				label={rateType}
				ariaLabel={t("wageBreakdown.openInWageSettings", { rateType })}
				dataTestId={`wage-breakdown-subtable-rate-type-link-${rowId}`}
				hoverCard={card}
			/>
		);
	}
	// An unlabelled segment has no name to hover.
	if (card === undefined || rateType.trim() === "") return rateType;
	return <RuleNameText label={rateType} card={card} dataTestId={`wage-breakdown-subtable-rate-type-name-${rowId}`} />;
};
